import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { mdiShieldCheck } from '@mdi/js';

import { Chip, ChipGroup } from 'common/components/Chip';
import { ICertificateManagementListItemFragment } from 'graphql/types';
import { TableCell, TableRowMedia } from 'common/components/Table';
import { getExpiryDisplay } from 'common/utils/expiryScale';
import { getScoringDisplay } from 'common/utils/scoring';
import { getUrl } from 'route/utils/getUrl';
import { Checkbox } from 'common/components/Checkbox';
import { ItemMedia } from 'common/components/ItemMedia';
import { Icon } from 'common/components/Icon';
import { ExtraCategoryLabels } from 'organisation/components/ExtraCategoryLabels';

interface IProps {
    certificate: ICertificateManagementListItemFragment;
    isEditable?: boolean;
    isSelected?: boolean;
    onSelect?(): void;
}

export const CertificatesManagementTableRow = ({
    certificate,
    isEditable,
    isSelected,
    onSelect,
}: IProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [translate] = useTranslation();
    const {
        id,
        name,
        thumbnail,
        expiry,
        expiryScale,
        scoring,
        isExternalCertificate,
        extraCategoryValues,
    } = certificate;

    const image = thumbnail?.url || '';

    const expiryText =
        expiry && expiryScale ? getExpiryDisplay(expiry, expiryScale) : '-';

    const scoringText = getScoringDisplay(scoring) || '-';

    const handleRowClick = () => {
        const searchParams = new URLSearchParams(location.search);
        const url = `${getUrl('CERTIFICATES_EDIT', { id })}?${searchParams}`;

        navigate(url);
    };

    return (
        <TableRowMedia
            hover
            key={id}
            selected={isSelected}
            sx={{ cursor: 'pointer' }}
            onClick={handleRowClick}
        >
            {isEditable && (
                <TableCell>
                    <Checkbox
                        checked={isSelected}
                        onClick={(e) => {
                            onSelect?.();

                            e.stopPropagation();
                        }}
                    />
                </TableCell>
            )}

            <TableCell sx={{ width: '1px' }}>
                <ItemMedia color="primary" image={image || ''} size="small">
                    <Icon path={mdiShieldCheck} size="2rem" sizeSm="3rem" />
                </ItemMedia>
            </TableCell>

            <TableCell>
                {name}
                <ChipGroup sx={{ mt: 0.5 }}>
                    {isExternalCertificate && (
                        <Chip
                            label={translate(
                                'certificatesManagementPage.externalChip'
                            )}
                        />
                    )}

                    {!!extraCategoryValues?.length && (
                        <ExtraCategoryLabels
                            chipProps={{ size: 'small' }}
                            extraCategoryValues={extraCategoryValues}
                        />
                    )}
                </ChipGroup>
            </TableCell>
            <TableCell colSpan={2}>{expiryText}</TableCell>
            <TableCell>{scoringText}</TableCell>
        </TableRowMedia>
    );
};
